import { CircularProgress } from '@mui/material';

/**
 * InlineLoader component - Small spinner for inline contexts
 *
 * Use this for loading indicators that appear inline with content,
 * such as within buttons, cards, or list items.
 *
 * @example
 * ```tsx
 * // In a button
 * <Button startIcon={isLoading && <InlineLoader size={16} />}>
 *   Save
 * </Button>
 *
 * // Inline in text
 * <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
 *   <InlineLoader size={20} />
 *   <Typography>Processing...</Typography>
 * </Box>
 *
 * // Inherit parent color
 * <Box sx={{ color: 'error.main' }}>
 *   <InlineLoader color="inherit" />
 * </Box>
 * ```
 */

export interface InlineLoaderProps {
  /**
   * Size of the spinner in pixels
   * @default 20
   */
  size?: number;

  /**
   * Color of the spinner
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'inherit';

  /**
   * Thickness of the circle stroke
   * @default 3.6
   */
  thickness?: number;
}

export const InlineLoader = ({
  size = 20,
  color = 'primary',
  thickness = 3.6,
}: InlineLoaderProps) => {
  return <CircularProgress size={size} color={color} thickness={thickness} />;
};
